import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  coverImage?: string;
  category: string;
  tags: string[];
  author: string;
  authorAvatar?: string;
  readTime: number;
  publishedAt?: string;
}

const BlogPage = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/blog`);
        const allPosts = data?.data || [];
        setPosts(allPosts);
        // Extract unique categories
        const cats = [...new Set(allPosts.map((p: BlogPost) => p.category))] as string[];
        setCategories(cats);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = useMemo(() => {
    let result = posts;

    // Filter by category
    if (activeCategory !== 'All') {
      result = result.filter(p => p.category === activeCategory);
    }

    // Filter by tag
    if (activeTag) {
      result = result.filter(p => p.tags.includes(activeTag));
    }

    // Filter by search query (title match)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(p => p.title.toLowerCase().includes(query));
    }

    return result;
  }, [posts, activeCategory, activeTag, searchQuery]);

  const handleTagClick = (tag: string) => {
    if (activeTag === tag) {
      setActiveTag(null);
    } else {
      setActiveTag(tag);
    }
  };

  const handleCategoryClick = (cat: string) => {
    setActiveCategory(cat);
    setActiveTag(null); // Reset tag filter when changing category
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6" data-aos="slide-right">
              Blog
            </h1>
            <p className="text-xl text-gray-100" data-aos="slide-left" data-aos-delay="100">
              Insights, tutorials, and updates from our team
            </p>
          </div>
        </div>
      </section>

      {/* Posts Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {/* Search Input */}
          <div className="max-w-md mx-auto mb-6" data-aos="fade-up">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles by title..."
                className="w-full px-4 py-2.5 pl-10 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4 justify-center" data-aos="fade-up">
              <button
                onClick={() => handleCategoryClick('All')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === 'All'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryClick(cat)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeCategory === cat
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Active Tag Indicator */}
          {activeTag && (
            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="text-sm text-gray-500">Filtered by tag:</span>
              <button
                onClick={() => setActiveTag(null)}
                className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm font-medium hover:bg-primary-100 transition-colors"
              >
                {activeTag}
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-xl h-80 animate-pulse" />
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary-50 rounded-xl flex items-center justify-center">
                <i className="fas fa-newspaper text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">
                {searchQuery || activeTag ? 'No Matching Posts' : 'No Posts Yet'}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                {searchQuery || activeTag
                  ? 'Try adjusting your search or filter criteria.'
                  : 'We are working on some great content. Check back soon for articles and updates.'}
              </p>
              {(searchQuery || activeTag) && (
                <button
                  onClick={() => { setSearchQuery(''); setActiveTag(null); setActiveCategory('All'); }}
                  className="mt-4 px-4 py-2 text-sm font-medium text-primary hover:text-primary-800 transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <div
                  key={post.id}
                  className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all"
                  data-aos="fade-up"
                >
                  {/* Cover Image */}
                  <Link to={`/blog/${post.slug}`}>
                    <div className="aspect-video bg-gray-100 overflow-hidden">
                      {post.coverImage ? (
                        <img
                          src={post.coverImage}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-primary/5">
                          <i className="fas fa-newspaper text-3xl text-primary/20"></i>
                        </div>
                      )}
                    </div>
                  </Link>

                  <div className="p-5">
                    {/* Category Badge */}
                    <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-primary-50 text-primary-600 rounded-full mb-3">
                      {post.category}
                    </span>

                    {/* Title */}
                    <Link to={`/blog/${post.slug}`}>
                      <h3 className="text-lg font-heading font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                    </Link>

                    {/* Excerpt */}
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{post.excerpt}</p>

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {post.tags.slice(0, 3).map(tag => (
                          <button
                            key={tag}
                            onClick={(e) => { e.preventDefault(); handleTagClick(tag); }}
                            className={`px-2 py-0.5 text-xs rounded-md transition-colors ${
                              activeTag === tag
                                ? 'bg-primary-100 text-primary-700 font-medium'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                            }`}
                          >
                            {tag}
                          </button>
                        ))}
                        {post.tags.length > 3 && (
                          <span className="px-2 py-0.5 text-xs text-gray-400">+{post.tags.length - 3}</span>
                        )}
                      </div>
                    )}

                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{post.author}</span>
                      <div className="flex items-center gap-3">
                        {post.publishedAt && (
                          <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                        )}
                        <span>{post.readTime} min read</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
